import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";
import { BankAccountStatus, FinanceReceiverStatus } from "@debitroom-software/types";
import { NavigationBar } from "@hugotomazi/capacitor-navigation-bar";
import { isAxiosError } from "axios";
import { defineStore } from "pinia";
import { nextTick } from "vue";

import getFinanceReceiverOverview from "@/modules/financeReceiver/api/getFinanceReceiverOverview";
import {
  CreditLimitRequestStep,
  CreditLimitRequestSteps,
  FinanceReceiverOnboardingStep,
  FinanceReceiverOverview,
  SepaContractUpdateStep
} from "@/modules/financeReceiver/types";
import dayjs from "@/shared/plugins/dayjs";

export const useFinanceReceiverStore = defineStore("financeReceiver", {
  persist: true,
  state: (): {
    data?: FinanceReceiverOverview;
    lastFetchedAt?: string;
    hasError: boolean;
    errorCode?: number;
    sepaContractUpdateStep: SepaContractUpdateStep;
    onboardingStep: FinanceReceiverOnboardingStep;
    creditLimitRequestStep: CreditLimitRequestStep;
  } => ({
    hasError: false,
    errorCode: undefined,
    data: undefined,
    lastFetchedAt: undefined,
    sepaContractUpdateStep: SepaContractUpdateStep.Sepa,
    onboardingStep: FinanceReceiverOnboardingStep.Welcome,
    creditLimitRequestStep: CreditLimitRequestStep.Quote
  }),
  actions: {
    async fetchFinanceReceiver() {
      if (this._shouldFetchData()) {
        await this._fetchFinanceReceiverData();
      }
      setTimeout(async () => {
        await SplashScreen.hide();
        await nextTick();
        setTimeout(() => {
          if (Capacitor.isPluginAvailable("StatusBar")) {
            StatusBar.setBackgroundColor({ color: "#2A455E" });
          }

          if (Capacitor.isPluginAvailable("NavigationBar")) {
            NavigationBar.setTransparency({ isTransparent: true });
          }
        }, 10);
      }, 500);
    },
    async forceFetchFinanceReceiver() {
      await this._fetchFinanceReceiverData();
    },
    async _fetchFinanceReceiverData() {
      try {
        const response = await getFinanceReceiverOverview();
        if (response) {
          this._setFinanceReceiverData(response.data);
          this.hasError = false;
          this.errorCode = undefined;
        } else {
          this.hasError = true;
          this.errorCode = 500;
        }
      } catch (error) {
        this.data = undefined;
        this.hasError = true;
        if (!isAxiosError(error)) {
          throw error;
        }
        if (!error.response?.status) {
          throw error;
        }
        if (error.response?.status === 401 || error.response?.status === 403) {
          return;
        }
        this.errorCode = error.response?.status;
        throw error;
      }
    },
    _setSepaContractUpdateStep(data: FinanceReceiverOverview) {
      if (!data.sepaContractRequest) {
        return undefined;
      }
      for (const enumName of Object.values(SepaContractUpdateStep)) {
        const step = data.sepaContractRequest.steps[enumName as keyof typeof data.sepaContractRequest.steps];
        if (step && !step.isCompleted) {
          this.sepaContractUpdateStep = enumName;
          return;
        }
      }
    },
    _setOnboardingStep(data: FinanceReceiverOverview) {
      if (!data.onboarding) {
        return undefined;
      }
      for (const enumName of Object.values(FinanceReceiverOnboardingStep)) {
        if (!data.onboarding.steps[enumName].isCompleted) {
          this.onboardingStep = enumName;
          return;
        }
      }
    },
    _setCreditLimitRequestStep(data: FinanceReceiverOverview) {
      if (!data.revolvingCreditFacility.creditLimitRequest) {
        return undefined;
      }
      if (data.revolvingCreditFacility.creditLimitRequest.isCompleted) {
        this.creditLimitRequestStep = CreditLimitRequestStep.Complete;
        return;
      }
      for (const enumName of Object.values(CreditLimitRequestStep)) {
        if (enumName === CreditLimitRequestStep.Complete) {
          continue;
        }
        const step = data.revolvingCreditFacility.creditLimitRequest.steps[enumName as keyof CreditLimitRequestSteps];
        if (step && !step.isCompleted) {
          this.creditLimitRequestStep = enumName;
          return;
        }
      }
    },
    _shouldFetchData(): boolean {
      return !this.lastFetchedAt || dayjs().diff(this.lastFetchedAt, "minutes") >= 5;
    },
    _setFinanceReceiverData(data: FinanceReceiverOverview) {
      this.data = data;
      this._setSepaContractUpdateStep(data);
      this._setOnboardingStep(data);
      this._setCreditLimitRequestStep(data);
      this.lastFetchedAt = dayjs().toISOString();
    }
  },
  getters: {
    isOffboarded(state) {
      return Boolean(state.data?.status === FinanceReceiverStatus.Offboarded);
    },
    isOnboarding(state) {
      return Boolean(state.data?.onboarding);
    },
    getLoanById: (state) => (id: string) => {
      const { revolvingCreditFacility } = state.data || {};
      if (!revolvingCreditFacility) {
        return undefined;
      }
      return (
        revolvingCreditFacility.activeLoansWithAttention.find((loan) => loan.id === id) ||
        revolvingCreditFacility.activeLoans.find((loan) => loan.id === id) ||
        revolvingCreditFacility.inactiveLoans.find((loan) => loan.id === id)
      );
    },
    hasBankAccounts(state) {
      return (state.data?.bankAccounts.length || 0) > 0;
    },
    hasSepaAccount(state) {
      return state.data?.bankAccounts.some((acc) => acc.isSepa);
    },
    getMostRecentSepaBankAccount(state) {
      return state.data?.bankAccounts
        .filter((acc) => acc.isSepa)
        .sort((a, b) => dayjs(b.sepaSignedAt).diff(dayjs(a.sepaSignedAt)))[0];
    },
    getBankAccountsExcludingMostRecentSepa(state) {
      const mostRecentSepaAccountId = state.data?.bankAccounts
        .filter((acc) => acc.isSepa)
        .sort((a, b) => dayjs(b.sepaSignedAt).diff(dayjs(a.sepaSignedAt)))[0]?.id;
      return state.data?.bankAccounts.filter((acc) => acc.id !== mostRecentSepaAccountId);
    },
    hasMultipleBankAccounts(state) {
      return (state.data?.bankAccounts.length || 0) > 1;
    },
    hasExpectedBankAccounts(state) {
      return state.data?.bankAccounts.some((acc) => acc.status === "Expected");
    },
    bankConnectionsRequiringAttention: (state) => {
      return state.data?.bankAccounts.filter(
        (account) =>
          account.isReauthorizationUpcoming ||
          account.isAuthorizationExpired ||
          account.status === BankAccountStatus.Expected
      );
    }
  }
});
